import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clientAuthService, ClientProfile, RegisterClientDto, LoginClientDto } from '@/services/client-auth.service';

interface ClientAuthContextData {
  client: ClientProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginClientDto) => Promise<void>;
  register: (data: RegisterClientDto) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextData>({} as ClientAuthContextData);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load client profile on mount
  useEffect(() => {
    loadClientProfile();
  }, []);

  const loadClientProfile = async () => {
    try {
      if (clientAuthService.isAuthenticated()) {
        const profile = await clientAuthService.getProfile();
        setClient(profile);
      }
    } catch (error) {
      console.error('Failed to load client profile:', error);
      // If token is invalid, logout
      clientAuthService.logout();
      setClient(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (data: LoginClientDto) => {
    setIsLoading(true);
    try {
      const response = await clientAuthService.login(data);
      setClient(response.client as ClientProfile);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterClientDto) => {
    setIsLoading(true);
    try {
      const response = await clientAuthService.register(data);
      setClient(response.client as ClientProfile);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clientAuthService.logout();
    setClient(null);
  };

  const refreshProfile = async () => {
    if (clientAuthService.isAuthenticated()) {
      try {
        const profile = await clientAuthService.getProfile();
        setClient(profile);
      } catch (error) {
        console.error('Failed to refresh profile:', error);
        logout();
      }
    }
  };

  return (
    <ClientAuthContext.Provider
      value={{
        client,
        isLoading,
        isAuthenticated: !!client,
        login,
        register,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext);

  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }

  return context;
}
